import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import PageHelp from '../../components/PageHelp';

interface EmailTemplate {
  id: string;
  name: string;
  description?: string;
  templateType: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  creator?: {
    firstName: string;
    lastName: string;
  };
}

const TEMPLATE_TYPES = [
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'PURCHASE_INVOICE', label: 'Purchase Invoice' },
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'EXPENSE', label: 'Expense' },
];

const EmailTemplates: React.FC = () => {
  const { user } = useAuthStore();
  const { hasPermission, loading: permissionsLoading } = usePermissions(user);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterTemplateType, setFilterTemplateType] = useState<string>('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateType: 'INVOICE',
    subject: '',
    htmlBody: '',
    textBody: '',
    isDefault: false,
  });

  useEffect(() => {
    fetchEmailTemplates();
  }, [filterTemplateType]);

  useEffect(() => {
    if (formData.templateType) {
      fetchPlaceholders(formData.templateType);
    }
  }, [formData.templateType]);

  const fetchEmailTemplates = async () => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (filterTemplateType) {
        params.templateType = filterTemplateType;
      }
      const response = await axios.get('/api/erp/email-templates', { params });
      setEmailTemplates(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching email templates:', err);
      toast.error('Failed to load email templates');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlaceholders = async (templateType: string) => {
    try {
      const response = await axios.get(`/api/erp/email-templates/placeholders/${templateType}`);
      setPlaceholders(response.data.data || []);
    } catch (err) {
      console.error('Error fetching placeholders:', err);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.post('/api/erp/email-templates', formData);
      if (response.data.success) {
        toast.success('Email template created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchEmailTemplates();
      } else {
        setError(response.data.error || 'Failed to create email template');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to create email template';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      templateType: template.templateType,
      subject: template.subject,
      htmlBody: template.htmlBody,
      textBody: template.textBody || '',
      isDefault: template.isDefault,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    setError(null);

    try {
      const response = await axios.put(`/api/erp/email-templates/${selectedTemplate.id}`, formData);
      if (response.data.success) {
        toast.success('Email template updated successfully');
        setShowEditModal(false);
        setSelectedTemplate(null);
        resetForm();
        fetchEmailTemplates();
      } else {
        setError(response.data.error || 'Failed to update email template');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to update email template';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this email template?')) return;

    try {
      const response = await axios.delete(`/api/erp/email-templates/${id}`);
      if (response.data.success) {
        toast.success('Email template deleted successfully');
        fetchEmailTemplates();
      } else {
        toast.error(response.data.error || 'Failed to delete email template');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete email template');
    }
  };

  const handlePreview = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const insertPlaceholder = (placeholder: string, field: 'subject' | 'htmlBody' | 'textBody') => {
    const textarea = document.getElementById(field) as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      const newText = before + `{{${placeholder}}}` + after;
      setFormData({ ...formData, [field]: newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length + 4, start + placeholder.length + 4);
      }, 0);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      templateType: 'INVOICE',
      subject: '',
      htmlBody: '',
      textBody: '',
      isDefault: false,
    });
  };

  const getDefaultTemplate = (templateType: string): { subject: string; htmlBody: string; textBody: string } => {
    if (templateType === 'INVOICE') {
      return {
        subject: 'Invoice {{document.number}} from {{company.name}}',
        htmlBody: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
</head>
<body style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2>Invoice {{document.number}}</h2>
  <p>Dear {{client.name}},</p>
  <p>Please find attached invoice {{document.number}} for your records.</p>
  <p><strong>Invoice Date:</strong> {{document.date}}</p>
  <p><strong>Due Date:</strong> {{document.dueDate}}</p>
  <p><strong>Total Amount:</strong> {{document.total}}</p>
  <p>Thank you for your business!</p>
  <p>Best regards,<br>{{company.name}}</p>
</body>
</html>`,
        textBody: `Invoice {{document.number}}

Dear {{client.name}},

Please find attached invoice {{document.number}}.

Invoice Date: {{document.date}}
Due Date: {{document.dueDate}}
Total Amount: {{document.total}}

Thank you for your business!

Best regards,
{{company.name}}`,
      };
    }
    return {
      subject: '{{document.number}} from {{company.name}}',
      htmlBody: '<p>{{document.number}}</p>',
      textBody: '{{document.number}}',
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading email templates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <PageHelp articleKey="erp_email_template" />
        </div>
        <div>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage custom email templates for invoices and other documents
          </p>
        </div>
        {hasPermission('erp_financial_report', 'write') && (
          <button
            onClick={() => {
              const defaultTemplate = getDefaultTemplate('INVOICE');
              setFormData({
                ...formData,
                subject: defaultTemplate.subject,
                htmlBody: defaultTemplate.htmlBody,
                textBody: defaultTemplate.textBody,
              });
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            New Email Template
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Template Type</label>
          <select
            value={filterTemplateType}
            onChange={(e) => setFilterTemplateType(e.target.value)}
            className="block w-full max-w-xs border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Types</option>
            {TEMPLATE_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {emailTemplates.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">No email templates found. Create your first template to get started.</p>
          {hasPermission('erp_financial_report', 'write') && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Your First Email Template
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Template Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {emailTemplates.map(template => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{template.name}</div>
                    {template.description && (
                      <div className="text-sm text-gray-500">{template.description}</div>
                    )}
                    {template.isDefault && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {TEMPLATE_TYPES.find(t => t.value === template.templateType)?.label || template.templateType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      template.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handlePreview(template)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Preview
                    </button>
                    {hasPermission('erp_financial_report', 'write') && (
                      <>
                        <button
                          onClick={() => handleEdit(template)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        {hasPermission('erp_financial_report', 'delete') && (
                          <button
                            onClick={() => handleDelete(template.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create Email Template</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Template Type *</label>
                  <select
                    required
                    value={formData.templateType}
                    onChange={(e) => {
                      const defaultTemplate = getDefaultTemplate(e.target.value);
                      setFormData({ 
                        ...formData, 
                        templateType: e.target.value,
                        subject: defaultTemplate.subject,
                        htmlBody: defaultTemplate.htmlBody,
                        textBody: defaultTemplate.textBody,
                      });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {TEMPLATE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label className="ml-2 text-sm text-gray-700">Set as default template for this type</label>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Email Subject *</label>
                  <div className="text-sm text-gray-500">
                    <div className="mt-1 flex flex-wrap gap-1">
                      {placeholders.map(placeholder => (
                        <button
                          key={placeholder}
                          type="button"
                          onClick={() => insertPlaceholder(placeholder, 'subject')}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                          title={`Insert {{${placeholder}}}`}
                        >
                          {placeholder}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <input
                  id="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter email subject with {{placeholders}}"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">HTML Body *</label>
                  <div className="text-sm text-gray-500">
                    <div className="mt-1 flex flex-wrap gap-1">
                      {placeholders.map(placeholder => (
                        <button
                          key={placeholder}
                          type="button"
                          onClick={() => insertPlaceholder(placeholder, 'htmlBody')}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                          title={`Insert {{${placeholder}}}`}
                        >
                          {placeholder}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <textarea
                  id="htmlBody"
                  required
                  value={formData.htmlBody}
                  onChange={(e) => setFormData({ ...formData, htmlBody: e.target.value })}
                  rows={15}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 font-mono text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter HTML email body with {{placeholders}}"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Plain Text Body (Optional)</label>
                  <div className="text-sm text-gray-500">
                    <div className="mt-1 flex flex-wrap gap-1">
                      {placeholders.map(placeholder => (
                        <button
                          key={placeholder}
                          type="button"
                          onClick={() => insertPlaceholder(placeholder, 'textBody')}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                          title={`Insert {{${placeholder}}}`}
                        >
                          {placeholder}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <textarea
                  id="textBody"
                  value={formData.textBody}
                  onChange={(e) => setFormData({ ...formData, textBody: e.target.value })}
                  rows={10}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 font-mono text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter plain text email body with {{placeholders}}"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Create Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal - Similar to Create */}
      {showEditModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Email Template</h3>
              <button onClick={() => { setShowEditModal(false); setSelectedTemplate(null); resetForm(); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Template Type *</label>
                  <select
                    required
                    value={formData.templateType}
                    onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {TEMPLATE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label className="ml-2 text-sm text-gray-700">Set as default template for this type</label>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Email Subject *</label>
                  <div className="text-sm text-gray-500">
                    <div className="mt-1 flex flex-wrap gap-1">
                      {placeholders.map(placeholder => (
                        <button
                          key={placeholder}
                          type="button"
                          onClick={() => insertPlaceholder(placeholder, 'subject')}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                          title={`Insert {{${placeholder}}}`}
                        >
                          {placeholder}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <input
                  id="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">HTML Body *</label>
                  <div className="text-sm text-gray-500">
                    <div className="mt-1 flex flex-wrap gap-1">
                      {placeholders.map(placeholder => (
                        <button
                          key={placeholder}
                          type="button"
                          onClick={() => insertPlaceholder(placeholder, 'htmlBody')}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                          title={`Insert {{${placeholder}}}`}
                        >
                          {placeholder}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <textarea
                  id="htmlBody"
                  required
                  value={formData.htmlBody}
                  onChange={(e) => setFormData({ ...formData, htmlBody: e.target.value })}
                  rows={15}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 font-mono text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Plain Text Body (Optional)</label>
                  <div className="text-sm text-gray-500">
                    <div className="mt-1 flex flex-wrap gap-1">
                      {placeholders.map(placeholder => (
                        <button
                          key={placeholder}
                          type="button"
                          onClick={() => insertPlaceholder(placeholder, 'textBody')}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                          title={`Insert {{${placeholder}}}`}
                        >
                          {placeholder}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <textarea
                  id="textBody"
                  value={formData.textBody}
                  onChange={(e) => setFormData({ ...formData, textBody: e.target.value })}
                  rows={10}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 font-mono text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedTemplate(null); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Update Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Preview: {selectedTemplate.name}</h3>
              <button onClick={() => { setShowPreviewModal(false); setSelectedTemplate(null); }} className="text-gray-400 hover:text-gray-500">
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div className="border rounded p-4 bg-gray-50">
                <div className="text-sm text-gray-600 mb-2">Subject:</div>
                <div className="bg-white p-2 rounded border text-sm">{selectedTemplate.subject}</div>
              </div>
              <div className="border rounded p-4 bg-gray-50">
                <div className="text-sm text-gray-600 mb-2">HTML Body:</div>
                <pre className="bg-white p-4 rounded border overflow-auto text-xs max-h-96">
                  {selectedTemplate.htmlBody}
                </pre>
              </div>
              {selectedTemplate.textBody && (
                <div className="border rounded p-4 bg-gray-50">
                  <div className="text-sm text-gray-600 mb-2">Plain Text Body:</div>
                  <pre className="bg-white p-4 rounded border overflow-auto text-xs max-h-96">
                    {selectedTemplate.textBody}
                  </pre>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => { setShowPreviewModal(false); setSelectedTemplate(null); }}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplates;
